import { PASSWORD_MIN_LENGTH } from '@mcgames/contracts';
import { Button, Spinner, TextInput } from '@mcgames/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAcceptInvite, useErrorText, useInviteInfo } from '../api/hooks.ts';
import { PublicLayout } from '../layout/PublicLayout.tsx';
import styles from './Page.module.css';
import { ReleaseDownloads } from './ReleaseDownloads.tsx';

const USERNAME_PATTERN = /^[a-z0-9_.-]{3,32}$/;

type FieldErrors = { username?: string; password?: string; confirm?: string };

/** Invite landing page: validity check, account form, then the download links. */
export function InvitePage() {
  const { t } = useTranslation('admin');
  const { token = '' } = useParams();
  const info = useInviteInfo(token);
  const errorText = useErrorText();
  const [created, setCreated] = useState(false);

  let body;
  if (created) {
    body = <Created />;
  } else if (info.isPending) {
    body = <Spinner />;
  } else if (info.isError) {
    body = (
      <p className={styles['error']} role="alert">
        {errorText(info.error)}
      </p>
    );
  } else if (!info.data.valid) {
    body = (
      <p className={styles['text']} role="alert">
        {t('invite.invalid')}
      </p>
    );
  } else {
    body = <InviteForm token={token} onCreated={() => setCreated(true)} />;
  }
  return <PublicLayout wide={created}>{body}</PublicLayout>;
}

function Created() {
  const { t } = useTranslation('admin');
  return (
    <section className={styles['card']}>
      <h1 className={styles['title']}>{t('invite.createdTitle')}</h1>
      <p className={styles['text']}>{t('invite.createdMessage')}</p>
      <ReleaseDownloads />
    </section>
  );
}

function InviteForm({ token, onCreated }: { token: string; onCreated: () => void }) {
  const { t } = useTranslation('admin');
  const errorText = useErrorText();
  const accept = useAcceptInvite(token);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  function validate(name: string): FieldErrors {
    const found: FieldErrors = {};
    if (!USERNAME_PATTERN.test(name)) found.username = t('invite.validation.username');
    if (password.length < PASSWORD_MIN_LENGTH) {
      found.password = t('invite.validation.passwordShort');
    }
    if (password !== confirm) found.confirm = t('invite.validation.passwordMismatch');
    return found;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const name = username.trim().toLowerCase();
    const found = validate(name);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    accept.mutate({ username: name, password }, { onSuccess: onCreated });
  }

  return (
    <form className={`${styles['card']} ${styles['form']}`} onSubmit={submit} noValidate>
      <h1 className={styles['title']}>{t('invite.title')}</h1>
      <p className={styles['text']}>{t('invite.intro')}</p>
      <div>
        <TextInput
          label={t('invite.username')}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={64}
          required
          {...(errors.username !== undefined && { error: errors.username })}
        />
        <span className={styles['hint']}>{t('invite.usernameHint')}</span>
      </div>
      <div>
        <TextInput
          label={t('invite.password')}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
          {...(errors.password !== undefined && { error: errors.password })}
        />
        <span className={styles['hint']}>{t('invite.passwordHint')}</span>
      </div>
      <TextInput
        label={t('invite.confirmPassword')}
        type="password"
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
        autoComplete="new-password"
        required
        {...(errors.confirm !== undefined && { error: errors.confirm })}
      />
      {accept.isError && (
        <p className={styles['error']} role="alert">
          {errorText(accept.error)}
        </p>
      )}
      <div className={styles['actions']}>
        <Button type="submit" disabled={accept.isPending}>
          {t('invite.submit')}
        </Button>
      </div>
    </form>
  );
}
